import React, { useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import AOS from "aos";
import "aos/dist/aos.css";
import {
  Calendar,
  Globe,
  User,
  Clock,
  Users,
  ArrowLeft,
  ExternalLink,
  CheckCircle2,
  Rocket,
  TrendingUp,
  Code,
  Award,
  Target,
  ArrowRight,
  Quote,
  Share2,
  Facebook,
  Twitter,
  Linkedin,
  Mail,
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { cn } from "../../lib/utils";
import { ProjectData } from "./ProjectData";

const ProjectDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const project = ProjectData.find((p) => p.id === id);

  useEffect(() => {
    AOS.init({
      duration: 1000,
      once: true,
      offset: 100,
    });
    window.scrollTo(0, 0);
  }, [id]);

  if (!project) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <h2 className="text-2xl font-bold mb-4">Project Not Found</h2>
            <p className="text-muted-foreground mb-6">
              The project you're looking for doesn't exist.
            </p>
            <Button asChild>
              <Link to="/projects">Back to Projects</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Get related projects (exclude current project)
  const relatedProjects = ProjectData.filter(
    (p) => p.id !== project.id && p.category === project.category
  ).slice(0, 3);

  const handleShare = (platform) => {
    const url = window.location.href;
    const title = project.title;

    const shareUrls = {
      twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
      email: `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(url)}`,
    };

    if (shareUrls[platform]) {
      window.open(shareUrls[platform], "_blank", "width=600,height=400");
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative py-20 md:py-28 bg-gradient-to-br from-background via-primary/5 to-background border-b overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03] dark:opacity-[0.05]">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `radial-gradient(circle at 2px 2px, hsl(var(--foreground)) 1px, transparent 0)`,
              backgroundSize: "50px 50px",
            }}
          />
        </div>

        <div className="container relative z-10">
          <Button
            variant="ghost"
            onClick={() => navigate("/projects")}
            className="mb-8 group"
            data-aos="fade-right"
            data-aos-delay="100"
          >
            <ArrowLeft className="mr-2 h-4 w-4 group-hover:-translate-x-1 transition-transform" />
            Back to Projects
          </Button>

          <div className="max-w-4xl mx-auto">
            <div
              className="flex flex-wrap gap-2 mb-6"
              data-aos="fade-up"
              data-aos-delay="100"
            >
              <span
                className={cn(
                  "text-sm font-medium px-4 py-2 rounded-full",
                  project.status === "completed"
                    ? "bg-green-500/10 text-green-600 border border-green-500/20"
                    : "bg-yellow-500/10 text-yellow-600 border border-yellow-500/20"
                )}
              >
                {project.status === "completed" ? (
                  <span className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4" />
                    Completed
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    Upcoming
                  </span>
                )}
              </span>
              <span className="text-sm font-medium text-primary bg-primary/10 px-4 py-2 rounded-full border border-primary/20">
                {project.category}
              </span>
            </div>

            <h1
              className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6"
              data-aos="fade-up"
              data-aos-delay="200"
            >
              {project.title}
            </h1>

            <p
              className="text-lg text-muted-foreground leading-relaxed mb-8"
              data-aos="fade-up"
              data-aos-delay="300"
            >
              {project.description}
            </p>

            <div
              className="flex flex-wrap items-center gap-6 text-muted-foreground"
              data-aos="fade-up"
              data-aos-delay="400"
            >
              <div className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                <span>{project.info.date}</span>
              </div>
              <div className="flex items-center gap-2">
                <Globe className="h-5 w-5" />
                <span>{project.info.location}</span>
              </div>
              {project.info.duration && (
                <div className="flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  <span>{project.info.duration}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 md:py-16">
        <div className="container">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <main className="lg:col-span-2 space-y-8">
              {/* Banner Image */}
              <div data-aos="fade-up" data-aos-delay="100">
                <img
                  src={project.bannerImage}
                  alt={project.title}
                  className="w-full h-[400px] md:h-[500px] object-cover rounded-xl shadow-lg"
                />
              </div>

              {/* Project Overview */}
              <Card className="border-border" data-aos="fade-up" data-aos-delay="200">
                <CardHeader>
                  <CardTitle className="text-2xl">Project Overview</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-lg leading-relaxed text-muted-foreground mb-6">
                    {project.subDescription}
                  </p>
                  <h3 className="text-xl font-semibold mb-4">{project.subTitle}</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {project.description}
                  </p>
                </CardContent>
              </Card>

              {/* Gallery Images */}
              {project.galleryImages && project.galleryImages.length > 0 && (
                <Card className="border-border" data-aos="fade-up" data-aos-delay="300">
                  <CardHeader>
                    <CardTitle className="text-2xl">Project Gallery</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-2 gap-4">
                      {project.galleryImages.map((img, idx) => (
                        <div
                          key={idx}
                          className="relative aspect-video overflow-hidden rounded-lg group cursor-pointer"
                        >
                          <img
                            src={img}
                            alt={`${project.title} - Image ${idx + 1}`}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                          />
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Technologies Used */}
              <Card className="border-border" data-aos="fade-up" data-aos-delay="400">
                <CardHeader>
                  <CardTitle className="text-2xl flex items-center gap-2">
                    <Code className="h-6 w-6 text-primary" />
                    Technologies Used
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-3">
                    {project.technologies.map((tech, idx) => (
                      <span
                        key={idx}
                        className="text-sm bg-primary/10 text-primary px-4 py-2 rounded-full border border-primary/20"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Results & Impact */}
              <Card className="border-border" data-aos="fade-up" data-aos-delay="500">
                <CardHeader>
                  <CardTitle className="text-2xl flex items-center gap-2">
                    <Target className="h-6 w-6 text-primary" />
                    {project.resultTitle}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {project.resultParagraphs.map((para, idx) => (
                    <p key={idx} className="text-muted-foreground leading-relaxed">
                      {para}
                    </p>
                  ))}
                </CardContent>
              </Card>

              {/* Project Stats */}
              {project.stats && (
                <Card className="border-border" data-aos="fade-up" data-aos-delay="600">
                  <CardHeader>
                    <CardTitle className="text-2xl flex items-center gap-2">
                      <TrendingUp className="h-6 w-6 text-primary" />
                      Key Metrics
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                      {project.stats.map((stat, idx) => (
                        <div
                          key={idx}
                          className="text-center p-4 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
                        >
                          <div className="text-3xl font-bold text-primary mb-2">
                            {stat.value}
                          </div>
                          <div className="text-sm text-muted-foreground">{stat.label}</div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Testimonial */}
              {project.testimonial && (
                <Card
                  className="border-border bg-gradient-to-br from-primary/10 to-primary/5"
                  data-aos="fade-up"
                  data-aos-delay="700"
                >
                  <CardContent className="p-8">
                    <Quote className="h-12 w-12 text-primary mb-4 opacity-50" />
                    <p className="text-lg italic text-foreground mb-4">
                      "{project.testimonial.quote}"
                    </p>
                    <div className="flex items-center gap-3">
                      <div className="h-12 w-12 rounded-full bg-primary/20 flex items-center justify-center">
                        <User className="h-6 w-6 text-primary" />
                      </div>
                      <div>
                        <p className="font-semibold">{project.testimonial.author}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Share Section */}
              <Card className="border-border" data-aos="fade-up" data-aos-delay="800">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Share2 className="h-5 w-5 text-primary" />
                      <span className="font-semibold">Share this project:</span>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleShare("facebook")}
                        className="h-10 w-10 p-0"
                      >
                        <Facebook className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleShare("twitter")}
                        className="h-10 w-10 p-0"
                      >
                        <Twitter className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleShare("linkedin")}
                        className="h-10 w-10 p-0"
                      >
                        <Linkedin className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleShare("email")}
                        className="h-10 w-10 p-0"
                      >
                        <Mail className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </main>

            {/* Sidebar */}
            <aside className="lg:col-span-1 space-y-6">
              {/* Project Info Card */}
              <Card
                className="border-border sticky top-24"
                data-aos="fade-left"
                data-aos-delay="100"
              >
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Award className="h-5 w-5 text-primary" />
                    Project Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <p className="text-sm font-medium mb-1">Client</p>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <User className="h-4 w-4" />
                      <span>{project.info.client}</span>
                    </div>
                  </div>

                  <div>
                    <p className="text-sm font-medium mb-1">Date</p>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Calendar className="h-4 w-4" />
                      <span>{project.info.date}</span>
                    </div>
                  </div>

                  {project.info.duration && (
                    <div>
                      <p className="text-sm font-medium mb-1">Duration</p>
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Clock className="h-4 w-4" />
                        <span>{project.info.duration}</span>
                      </div>
                    </div>
                  )}

                  {project.info.team && (
                    <div>
                      <p className="text-sm font-medium mb-1">Team Size</p>
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Users className="h-4 w-4" />
                        <span>{project.info.team}</span>
                      </div>
                    </div>
                  )}

                  <div>
                    <p className="text-sm font-medium mb-1">Location</p>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Globe className="h-4 w-4" />
                      <span>{project.info.location}</span>
                    </div>
                  </div>

                  {project.info.website && project.info.website !== "Coming Soon" && (
                    <div>
                      <p className="text-sm font-medium mb-1">Website</p>
                      <a
                        href={project.info.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-primary hover:underline"
                      >
                        <ExternalLink className="h-4 w-4" />
                        <span className="truncate">Visit Website</span>
                      </a>
                    </div>
                  )}

                  <div className="pt-4 border-t">
                    <p className="text-sm font-medium mb-2">Description</p>
                    <p className="text-sm text-muted-foreground">
                      {project.info.description}
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* CTA Card */}
              <Card
                className="border-primary/20 bg-primary/5"
                data-aos="fade-left"
                data-aos-delay="200"
              >
                <CardContent className="p-6 text-center space-y-4">
                  <Rocket className="h-12 w-12 text-primary mx-auto" />
                  <h3 className="font-semibold text-lg">Start Your Project</h3>
                  <p className="text-sm text-muted-foreground">
                    Ready to bring your idea to life? Let's discuss your project requirements.
                  </p>
                  <Button asChild className="w-full" size="lg">
                    <Link to="/contactUs">
                      Get In Touch
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            </aside>
          </div>
        </div>
      </section>

      {/* Related Projects */}
      {relatedProjects.length > 0 && (
        <section className="py-16 md:py-20 bg-muted/30 border-t">
          <div className="container">
            <div
              className="text-center mb-12"
              data-aos="fade-up"
              data-aos-delay="100"
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Related <span className="text-primary">Projects</span>
              </h2>
              <div className="w-16 h-1 bg-primary mx-auto rounded-full" />
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {relatedProjects.map((relatedProject, index) => (
                <Card
                  key={relatedProject.id}
                  data-aos="fade-up"
                  data-aos-delay={100 + index * 100}
                  className="border-border hover:border-primary/50 transition-all duration-300 hover:shadow-xl group overflow-hidden"
                >
                  <Link to={`/projects/${relatedProject.id}`}>
                    <div className="relative aspect-video overflow-hidden">
                      <img
                        src={relatedProject.bannerImage}
                        alt={relatedProject.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute top-4 left-4">
                        <span
                          className={cn(
                            "text-xs font-medium px-3 py-1 rounded-full backdrop-blur-sm",
                            relatedProject.status === "completed"
                              ? "bg-green-500/90 text-white"
                              : "bg-yellow-500/90 text-white"
                          )}
                        >
                          {relatedProject.status === "completed" ? "Completed" : "Upcoming"}
                        </span>
                      </div>
                    </div>
                    <CardHeader>
                      <CardTitle className="line-clamp-2 group-hover:text-primary transition-colors">
                        {relatedProject.title}
                      </CardTitle>
                      <CardDescription className="line-clamp-2">
                        {relatedProject.description}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4" />
                          <span>{relatedProject.info.date}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Link>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default ProjectDetails;
